import * as React from "react";

import styles from "./ListMember.module.css";

interface ComparatorPillProps {
  /** The comparator's label (see `listMemberLabels`). */
  label: string;
  /**
   * Hide the pill from assistive tech, for a pill drawn beside a value: the
   * operator already says it, so reading it before every value only repeats
   * it. Leave it read where the pill is all there is ("Not set").
   */
  decorative?: boolean;
}

/**
 * The comparator a member of an "is any of" / "is none of" list stands for, as
 * a pill drawn like the operator picker's.
 */
export function ComparatorPill({
  label,
  decorative = false,
}: ComparatorPillProps) {
  return (
    <span className={styles.pill} aria-hidden={decorative || undefined}>
      {label}
    </span>
  );
}

interface ListMemberProps {
  /** The comparator's label (see `listMemberLabels`). */
  comparator: string;
  /**
   * The value. Leave it out for the "Not set" member, which is the pill
   * alone.
   */
  children?: React.ReactNode;
}

/**
 * One member of an "is any of" / "is none of" list as an option row draws it:
 * the comparator it stands for, then the value ("equals nightly"). The "Not
 * set" member has no value, so it is the pill alone ("is not set"). The
 * pill is drawn in the operator picker's face, so the row reads as the
 * operator it stands for.
 *
 * The pill is read aloud only when it stands alone; beside a value the member
 * is named by its value.
 */
export function ListMember({ comparator, children }: ListMemberProps) {
  if (children === undefined) {
    return <ComparatorPill label={comparator} />;
  }
  return (
    <span className={styles.member}>
      <ComparatorPill label={comparator} decorative />
      {/* Only the gap separates the two on screen; the space separates them
          in the text, for copying and for find-in-page. */}{" "}
      <span className={styles.value}>{children}</span>
    </span>
  );
}
